/* jshint esversion: 6 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { showToast } from "../../showTost";

const DELIVERY_WINDOWS = ["09:00-12:00", "12:00-15:00", "15:00-18:00", "18:00-21:00"];

function toIso(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatDateLabel(value) {
  const s = String(value).slice(0, 10);
  const [y, m, d] = s.split("-").map(Number);
  if (!y) return s;
  return new Date(y, m - 1, d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}

function formatTabLabel(value) {
  const s = String(value).slice(0, 10);
  const [y, m, d] = s.split("-").map(Number);
  if (!y) return { day: "", rest: s };
  const dt = new Date(y, m - 1, d);
  return {
    day: dt.toLocaleDateString("en-IN", { weekday: "short" }),
    rest: dt.toLocaleDateString("en-IN", { day: "numeric", month: "short" }),
  };
}

export default function PharmacySlots() {
  const navigate = useNavigate();
  const [activeDate, setActiveDate] = useState("");
  const [selected, setSelected] = useState(null);

  const dateTabs = useMemo(() => {
    const tabs = [];
    const today = new Date();
    for (let i = 0; i < 7; i++) {
      const dt = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
      tabs.push(toIso(dt));
    }
    return tabs;
  }, []);

  useEffect(() => {
    try {
      const cart = JSON.parse(sessionStorage.getItem("pharmacy_cart") || "[]");
      if (!cart.length) {
        showToast({ title: "Select medicines first", id: "warning" });
        navigate("/pharmacy", { replace: true });
        return;
      }
    } catch (_) {
      navigate("/pharmacy", { replace: true });
      return;
    }
    setActiveDate((prev) => prev || dateTabs[0] || "");
  }, [dateTabs, navigate]);

  const pickDateTab = (iso) => {
    setActiveDate(iso);
    setSelected(null);
  };

  const goCheckout = () => {
    if (!selected) {
      showToast({ title: "Tap a time slot to continue", id: "warning" });
      return;
    }
    sessionStorage.setItem("pharmacy_slot", JSON.stringify(selected));
    navigate("/pharmacy/checkout", { state: { slot: selected } });
  };

  return (
    <div className="width-container py-8 pb-56">
      <Link to="/pharmacy" className="text-sm text-rm-primary underline mb-4 inline-block">← Back to medicines</Link>
      <h1 className="text-2xl font-bold mb-2">Choose delivery slot</h1>
      <p className="text-gray-600 mb-4">Pick a date, then tap a time. Partner pharmacy delivers in Hyderabad & Secunderabad.</p>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-6 -mx-1 px-1 lab-date-tabs">
        {dateTabs.map((iso) => {
          const { day, rest } = formatTabLabel(iso);
          const active = activeDate === iso;
          return (
            <button
              key={iso}
              type="button"
              onClick={() => pickDateTab(iso)}
              className={`flex-shrink-0 min-w-[72px] rounded-xl border px-3 py-2 text-center transition-colors ${active ? "bg-rm-primary text-white border-rm-primary shadow-sm" : "bg-white border-gray-200 hover:border-rm-primary"}`}
            >
              <span className="block text-xs font-medium opacity-90">{day}</span>
              <span className="block text-sm font-bold">{rest}</span>
            </button>
          );
        })}
      </div>

      <div className="border rounded-xl p-4 mb-4 bg-white shadow-sm">
        <h3 className="font-semibold text-lg mb-4">Delivery windows</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {DELIVERY_WINDOWS.map((win) => {
            const key = `${activeDate}-${win}`;
            const active = selected?.key === key;
            return (
              <button
                key={key}
                type="button"
                className={`text-sm px-3 py-3 rounded-lg border font-medium transition-colors ${active ? "bg-rm-primary text-white border-rm-primary" : "bg-white hover:border-rm-primary hover:bg-teal-50"}`}
                onClick={() => setSelected({
                  key,
                  delivery_date: activeDate,
                  delivery_slot: win,
                })}
              >
                {win.replace("-", "–")}
              </button>
            );
          })}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t p-4 shadow-lg z-[1300] safe-bottom">
        <div className="width-container max-w-3xl mx-auto pointer-events-auto">
          {selected ? (
            <p className="text-sm text-gray-600 mb-2">
              Selected: <strong>{formatDateLabel(selected.delivery_date)}</strong> · {String(selected.delivery_slot).replace("-", "–")}
            </p>
          ) : (
            <p className="text-sm text-amber-700 mb-2">Select a date tab and time slot above</p>
          )}
          <button
            type="button"
            className="rm-btn-primary w-full py-3 text-base font-semibold disabled:opacity-50"
            disabled={!selected}
            onClick={goCheckout}
          >
            Continue to checkout
          </button>
        </div>
      </div>
    </div>
  );
}
